import { ApplicationStatus, VerifyMethodType } from "shared/types";
import { ReasonCode } from "types/landlord/reports";
import { AdditionalData } from "../../types/verified/bank-statment";

const DECISIONED_OR_FROZEN_APPLICATION_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Approved,
  ApplicationStatus.ConditionalApproved,
  ApplicationStatus.Rejected,
  ApplicationStatus.Canceled
];

const DECISIONED_APPLICATION_REASON_PER_STATUS: Partial<
  Record<ApplicationStatus, string>
> = {
  [ApplicationStatus.Approved]: "approved",
  [ApplicationStatus.ConditionalApproved]: "approved with conditions",
  [ApplicationStatus.Rejected]: "rejected",
  [ApplicationStatus.Canceled]: "canceled"
};

const getGenericRejectionChecks = (
  additionalData: AdditionalData | null,
  documentType: VerifyMethodType
) => [
  {
    condition: additionalData?.type_mismatch,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: `The document is not a ${documentType?.replaceAll("_", " ")}`
    }
  },
  {
    condition: additionalData?.is_fraudulent,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Fraudulent activity detected"
    }
  },
  {
    condition: additionalData?.high_risk_level,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "High risk level"
    }
  },
  {
    condition: additionalData?.low_trust_score,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Low trust score"
    }
  },
  {
    condition: additionalData?.low_quality_score,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "The file is of poor quality"
    }
  },
  {
    condition: additionalData?.is_name_present === false,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "No name present on document"
    }
  }
];

// For any document verified through Inscribe
export const getGenericReasonCodes = (
  additionalData: AdditionalData,
  documentType: VerifyMethodType
) =>
  getGenericRejectionChecks(additionalData, documentType).reduce<ReasonCode[]>(
    (acc, check) => {
      if (check.condition) acc.push(check.reasonCode);
      return acc;
    },
    []
  );

const REJECTION_REASON_TYPE_SPECIFIC: Partial<
  Record<VerifyMethodType, Partial<Record<string, string>>>
> = {
  [VerifyMethodType.W2]: {
    type_mismatch: "Wrong document type",
    not_w2: "Tax form is not a W2",
    wrong_year: "W2 is for the wrong tax year",
    is_name_present: "No employee name detected on document",
    is_employer_name_present: "No employer name detected on document"
  },
  [VerifyMethodType.Paystub]: {
    type_mismatch: "The document is not a paystub",
    missing_period: "Pay period is missing",
    is_name_present: "No employee name present on document",
    is_employer_name_present: "No employer name present on document",
    no_amounts_extracted: "No amounts could be extracted from the document"
  }
};

const REJECTION_REASON_COMMON: Record<string, string> = {
  is_fraudulent: "Fraudulent activity detected",
  high_risk_level: "High risk level",
  low_trust_score: "Low trust score",
  low_quality_score: "The file is of poor quality",
  missing_period: "Missing period",
  no_amounts_extracted: "No income amount found"
};

export const rejectionReasonsToIssues = (
  reasons: string[] | undefined | null,
  verifyType: VerifyMethodType
): ReasonCode[] => {
  const typeMap = REJECTION_REASON_TYPE_SPECIFIC[verifyType] ?? {};
  return (reasons ?? []).reduce<ReasonCode[]>((acc, reason) => {
    const description = typeMap[reason] ?? REJECTION_REASON_COMMON[reason];
    if (description) acc.push({ code: "", confidence: "HIGH", description });
    return acc;
  }, []);
};

export const getActionsDisabledReason = ({
  canEditIncome,
  hasInProgressReverify,
  hasFailedReverify,
  isIncomeIncomplete,
  applicationStatus
}: {
  canEditIncome: boolean;
  hasInProgressReverify: boolean;
  hasFailedReverify: boolean;
  isIncomeIncomplete: boolean;
  applicationStatus?: ApplicationStatus;
}): {
  areActionsDisabled: boolean;
  editDisableReason: string;
  reverifyDisableReason: string;
} => {
  const isApplicationDecisionedOrFrozen = applicationStatus
    ? DECISIONED_OR_FROZEN_APPLICATION_STATUSES.includes(applicationStatus)
    : false;
  const areActionsDisabled =
    !canEditIncome ||
    isIncomeIncomplete ||
    hasInProgressReverify ||
    hasFailedReverify ||
    isApplicationDecisionedOrFrozen;

  const buildReasonMessage = (action: "edit" | "re-verify") => {
    if (!canEditIncome) return "You don't have permission for this action";
    if (isApplicationDecisionedOrFrozen) {
      const verb =
        DECISIONED_APPLICATION_REASON_PER_STATUS[
          applicationStatus as ApplicationStatus
        ] || "in a final state";
      return `Cannot ${action} after the application is ${verb}`;
    }
    if (hasInProgressReverify)
      return `Cannot ${action} while a re-verification is in progress`;
    if (hasFailedReverify)
      return `Cannot ${action} while a re-verification has failed`;
    if (isIncomeIncomplete)
      return `Cannot ${action} income until it is complete`;
    return "";
  };

  return {
    areActionsDisabled,
    editDisableReason: buildReasonMessage("edit"),
    reverifyDisableReason: buildReasonMessage("re-verify")
  };
};
